/**
 * Configuration persistence — file-based implementation for Node.js environments.
 *
 * Browser consumers should import from './config-types.ts' directly.
 */

import fs from 'node:fs'
import path from 'node:path'
import os from 'node:os'
import { ArbiError } from './errors.js'
import type { CliConfig, CliCredentials, ChatSession, ConfigStore } from './config-types.js'
import type { SessionStorageProvider, SessionKeys } from '@arbidocs/client'

// Re-export types for backward compatibility
export type { CliConfig, CliCredentials, ChatSession, ConfigStore } from './config-types.js'

// ── File-based implementation ────────────────────────────────────────────────

const DEFAULT_SESSION: ChatSession = {
  lastMessageExtId: null,
  conversationExtId: null,
  workspaceId: null,
}

export class FileConfigStore implements ConfigStore {
  private readonly configDir: string
  private readonly configFile: string
  private readonly credentialsFile: string
  private readonly sessionFile: string
  private readonly metadataFile: string

  constructor(configDir?: string) {
    // Resolution order:
    //   1. Explicit ``configDir`` arg (programmatic override).
    //   2. ``ARBI_CONFIG_DIR`` env var (operator-set, takes priority over
    //      any auto-detection so deployments can pin a path).
    //   3. ``./.arbi/`` in the current working directory, **if** it
    //      already contains a ``credentials.json``. This is what makes
    //      agent-style usage "just work": the openclaw container drops
    //      credentials into ``<workspace>/.arbi/`` and spawns tools with
    //      CWD = workspace, so plain ``arbi docs`` finds the right creds
    //      with zero prefixing. Requires the credentials file to exist so
    //      a stray empty ``.arbi/`` dir in some random project doesn't
    //      hijack a user's real ``~/.arbi`` session.
    //   4. ``~/.arbi`` (the historical default).
    const arbiBase =
      process.env.ARBI_CONFIG_DIR ??
      (fs.existsSync(path.join(process.cwd(), '.arbi', 'credentials.json'))
        ? path.join(process.cwd(), '.arbi')
        : path.join(os.homedir(), '.arbi'))
    const arbiId = process.env.ARBI_ID
    this.configDir = configDir ?? (arbiId ? path.join(arbiBase, arbiId) : arbiBase)
    this.configFile = path.join(this.configDir, 'config.json')
    this.credentialsFile = path.join(this.configDir, 'credentials.json')
    this.sessionFile = path.join(this.configDir, 'session.json')
    this.metadataFile = path.join(this.configDir, 'last-metadata.json')
  }

  private ensureConfigDir(): void {
    if (!fs.existsSync(this.configDir)) {
      fs.mkdirSync(this.configDir, { recursive: true, mode: 0o700 })
    }
  }

  private writeSecureFile(filePath: string, data: object): void {
    this.ensureConfigDir()
    fs.writeFileSync(filePath, JSON.stringify(data, null, 2) + '\n', { mode: 0o600 })
  }

  private readJsonFile<T>(filePath: string): T | null {
    try {
      const content = fs.readFileSync(filePath, 'utf-8')
      return JSON.parse(content) as T
    } catch {
      return null
    }
  }

  // ── Config ──────────────────────────────────────────────────────────────

  getConfig(): CliConfig | null {
    return this.readJsonFile<CliConfig>(this.configFile)
  }

  saveConfig(config: CliConfig): void {
    this.writeSecureFile(this.configFile, config)
  }

  updateConfig(updates: Partial<CliConfig>): void {
    const existing = this.getConfig() || ({} as Partial<CliConfig>)
    this.saveConfig({ ...existing, ...updates } as CliConfig)
  }

  requireConfig(): CliConfig {
    const config = this.getConfig()
    if (!config?.baseUrl) {
      throw new ArbiError('Not configured. Run: arbi config set-url <url>')
    }
    return config
  }

  // ── Credentials ─────────────────────────────────────────────────────────

  getCredentials(): CliCredentials | null {
    return this.readJsonFile<CliCredentials>(this.credentialsFile)
  }

  saveCredentials(creds: CliCredentials): void {
    this.writeSecureFile(this.credentialsFile, creds)
  }

  deleteCredentials(): void {
    try {
      fs.unlinkSync(this.credentialsFile)
    } catch {
      // Already gone
    }
  }

  requireCredentials(): CliCredentials {
    const creds = this.getCredentials()
    if (!creds) {
      // Agents bootstrapping with SUPPORT_API_KEY usually want `agent-create`
      // rather than the interactive `login`; mention both paths so automation
      // scripts see the scripted route first.
      const hint = process.env.SUPPORT_API_KEY
        ? 'Not logged in. Run: arbi agent-create --email agent-...@<domain>  (or: arbi login)'
        : 'Not logged in. Run: arbi login  (or arbi quickstart)'
      throw new ArbiError(hint)
    }
    return creds
  }

  /**
   * Returns a SessionStorageProvider backed by this store's
   * credentials file. Used to wire the auto-relogin middleware in
   * `@arbidocs/client` to the Node-side file store — without this, the
   * middleware falls back to IndexedDB which does not exist in Node
   * and re-login on 401 silently gives up.
   *
   * `getSession` reads the stored signing/server-session keys and
   * decodes them to the raw byte form the relogin handler expects.
   * `saveSession` writes a refreshed server session key back to the
   * credentials file on successful re-login so the next CLI
   * invocation picks it up.
   */
  getSessionStorageProvider(): SessionStorageProvider {
    const b64decode = (b64: string): Uint8Array =>
      typeof Buffer !== 'undefined'
        ? new Uint8Array(Buffer.from(b64, 'base64'))
        : Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
    const b64encode = (bytes: Uint8Array): string =>
      typeof Buffer !== 'undefined'
        ? Buffer.from(bytes).toString('base64')
        : btoa(String.fromCharCode(...bytes))
    const getCreds = (): CliCredentials | null => this.getCredentials()
    const saveCreds = (creds: CliCredentials): void => this.saveCredentials(creds)
    return {
      async getSession(): Promise<SessionKeys | null> {
        const creds = getCreds()
        if (!creds?.signingPrivateKeyBase64) return null
        return {
          signingPrivateKey: b64decode(creds.signingPrivateKeyBase64),
          serverSessionKey: creds.serverSessionKeyBase64
            ? b64decode(creds.serverSessionKeyBase64)
            : undefined,
        }
      },
      async saveSession(data: SessionKeys & { userExtId?: string }): Promise<void> {
        const existing = getCreds()
        if (!existing) return
        saveCreds({
          ...existing,
          signingPrivateKeyBase64: b64encode(data.signingPrivateKey),
          serverSessionKeyBase64: data.serverSessionKey
            ? b64encode(data.serverSessionKey)
            : existing.serverSessionKeyBase64,
          // A fresh server session = a fresh ``session.workspaces``
          // dict. The previous session's deposits do not transfer, so
          // wipe the client mirror to force re-deposit on next touch.
          accessToken: undefined,
          tokenTimestamp: undefined,
          openedWorkspaces: undefined,
        })
      },
    }
  }

  // ── Chat session ────────────────────────────────────────────────────────

  getChatSession(): ChatSession {
    return this.readJsonFile<ChatSession>(this.sessionFile) || { ...DEFAULT_SESSION }
  }

  saveChatSession(session: ChatSession): void {
    this.writeSecureFile(this.sessionFile, session)
  }

  updateChatSession(updates: Partial<ChatSession>): void {
    const existing = this.getChatSession()
    this.saveChatSession({ ...existing, ...updates })
  }

  clearChatSession(): void {
    this.saveChatSession({ ...DEFAULT_SESSION })
  }

  // ── Last metadata (for citation browsing) ────────────────────────────────

  saveLastMetadata(metadata: unknown): void {
    this.writeSecureFile(this.metadataFile, metadata as object)
  }

  loadLastMetadata(): unknown | null {
    return this.readJsonFile(this.metadataFile)
  }

  /**
   * Try to resolve config from multiple sources, in priority order:
   *
   * 1. Existing `~/.arbi/config.json` (highest priority)
   * 2. `ARBI_SERVER_URL` environment variable
   * 3. `.env` file in `searchDir` → read `VITE_DEPLOYMENT_DOMAIN`
   * 4. `public/config.json` in `searchDir` → read `deployment.domain`
   * 5. Default to `https://localhost`
   *
   * Returns `{ config, source }` where source describes where the config came from.
   * Saves auto-detected config to disk so subsequent calls use the fast path.
   */
  resolveConfigWithFallbacks(searchDir?: string): { config: CliConfig; source: string } {
    // 1. Existing config file
    const existing = this.getConfig()
    if (existing?.baseUrl) {
      return { config: existing, source: 'config' }
    }

    // 2. ARBI_SERVER_URL env var
    const envUrl = process.env.ARBI_SERVER_URL
    if (envUrl) {
      const domain = new URL(envUrl).hostname
      const config: CliConfig = { baseUrl: envUrl.replace(/\/$/, ''), deploymentDomain: domain }
      this.saveConfig(config)
      return { config, source: 'ARBI_SERVER_URL env var' }
    }

    const dir = searchDir || process.cwd()

    // 3. .env file → VITE_DEPLOYMENT_DOMAIN
    const dotenvConfig = this.readDotEnvDomain(path.join(dir, '.env'))
    if (dotenvConfig) {
      this.saveConfig(dotenvConfig)
      return { config: dotenvConfig, source: '.env file' }
    }

    // 4. public/config.json → deployment.domain
    const publicConfig = this.readPublicConfigDomain(path.join(dir, 'public', 'config.json'))
    if (publicConfig) {
      this.saveConfig(publicConfig)
      return { config: publicConfig, source: 'public/config.json' }
    }

    // 5. Default to https://localhost
    const config: CliConfig = { baseUrl: 'https://localhost', deploymentDomain: 'localhost' }
    this.saveConfig(config)
    return { config, source: 'default (https://localhost)' }
  }

  /**
   * Read VITE_DEPLOYMENT_DOMAIN from a .env file.
   */
  private readDotEnvDomain(filePath: string): CliConfig | null {
    try {
      const content = fs.readFileSync(filePath, 'utf-8')
      const match = content.match(/^VITE_DEPLOYMENT_DOMAIN\s*=\s*(.+)$/m)
      if (match) {
        const domain = match[1].trim().replace(/^["']|["']$/g, '')
        if (domain) {
          return { baseUrl: `https://${domain}`, deploymentDomain: domain }
        }
      }
    } catch {
      // File not found or unreadable
    }
    return null
  }

  /**
   * Read deployment.domain from a public/config.json file.
   */
  private readPublicConfigDomain(filePath: string): CliConfig | null {
    try {
      const content = fs.readFileSync(filePath, 'utf-8')
      const json = JSON.parse(content) as { deployment?: { domain?: string } }
      const domain = json?.deployment?.domain
      if (domain) {
        return { baseUrl: `https://${domain}`, deploymentDomain: domain }
      }
    } catch {
      // File not found or unparseable
    }
    return null
  }
}
